// src/components/Profile/ProfileTab.tsx
import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { useConnectGoogleChat, useDisconnectGoogleChat } from '@api/hooks/useGoogleChat';
import { useConnectSlack, useDisconnectSlack } from '@api/hooks/useSlack';
import { useConnectTeams, useDisconnectTeams } from '@api/hooks/useTeams';
import { useAuthStore } from '@store/authStore';
import { useNotificationStore } from '@store/notificationStore';
import { PasswordChangeSection } from './PasswordChangeSection';
import PlanGate from '@components/common/PlanGate';

export const ProfileTab = () => {
  const { currentUser } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const connectSlack = useConnectSlack();
  const disconnectSlack = useDisconnectSlack();
  const connectTeams = useConnectTeams();
  const disconnectTeams = useDisconnectTeams();
  const connectGoogleChat = useConnectGoogleChat();
  const disconnectGoogleChat = useDisconnectGoogleChat();
  const [isSaving, setIsSaving] = useState(false);
  const [editedData, setEditedData] = useState({
    email: '',
    first_name: '',
    last_name: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form data from current user
  useEffect(() => {
    if (currentUser) {
      setEditedData({
        email: currentUser.email || '',
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
      });
    }
  }, [currentUser]);

  useEffect(() => {
    const slackConnected = searchParams.get('slack_connected');
    const slackError = searchParams.get('slack_error');
    const teamsConnected = searchParams.get('teams_connected');
    const teamsError = searchParams.get('teams_error');
    const googleChatConnected = searchParams.get('google_chat_connected');
    const googleChatError = searchParams.get('google_chat_error');

    if (!slackConnected && !slackError && !teamsConnected && !teamsError && !googleChatConnected && !googleChatError) {
      return;
    }

    if (slackConnected === 'true') {
      addNotification({
        message: 'Slack connected successfully',
        type: 'success',
      });
    } else if (slackError) {
      addNotification({
        message: 'Slack connection failed. Try again.',
        type: 'error',
      });
    }
    if (teamsConnected === 'true') {
      addNotification({
        message: 'Teams connected successfully',
        type: 'success',
      });
    } else if (teamsError) {
      addNotification({
        message: 'Teams connection failed. Try again.',
        type: 'error',
      });
    }
    if (googleChatConnected === 'true') {
      addNotification({
        message: 'Google Chat connected successfully',
        type: 'success',
      });
    } else if (googleChatError) {
      addNotification({
        message: 'Google Chat connection failed. Try again.',
        type: 'error',
      });
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('slack_connected');
    nextParams.delete('slack_error');
    nextParams.delete('teams_connected');
    nextParams.delete('teams_error');
    nextParams.delete('google_chat_connected');
    nextParams.delete('google_chat_error');
    setSearchParams(nextParams, { replace: true });
  }, [addNotification, searchParams, setSearchParams]);

  // Handle input changes for editable fields
  const handleInputChange = (field: 'email' | 'first_name' | 'last_name', value: string) => {
    const newData = {
      ...editedData,
      [field]: value,
    };
    setEditedData(newData);

    // Check if there are changes from the original using NEW data
    const hasChanged =
      newData.email !== currentUser?.email ||
      newData.first_name !== currentUser?.first_name ||
      newData.last_name !== currentUser?.last_name;
    setHasChanges(hasChanged);
  };

  // Handle save
  const handleSave = async () => {
    if (!currentUser?.is_admin) return;

    setIsSaving(true);
    try {
      // TODO: Call PUT /auth/me endpoint when backend implements it
      const { setCurrentUser } = useAuthStore.getState();
      setCurrentUser({
        ...currentUser,
        email: editedData.email,
        first_name: editedData.first_name,
        last_name: editedData.last_name,
      });
      setHasChanges(false);
    } catch (err) {
      // Error handling
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (currentUser) {
      setEditedData({
        email: currentUser.email || '',
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
      });
      setHasChanges(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  const isAdmin = currentUser.is_admin;
  const slackConnection = currentUser.slack_connection;
  const teamsConnection = currentUser.teams_connection;
  const googleChatConnection = currentUser.google_chat_connection;

  const handleConnectSlack = async () => {
    const result = await connectSlack.mutateAsync('/profile');
    window.location.assign(result.authorization_url);
  };

  const handleConnectTeams = async () => {
    const result = await connectTeams.mutateAsync({
      returnPath: '/profile',
      mode: isAdmin ? 'admin' : 'user',
    });
    window.location.assign(result.authorization_url);
  };

  const handleConnectGoogleChat = async () => {
    const result = await connectGoogleChat.mutateAsync({
      returnPath: '/profile',
      mode: isAdmin ? 'admin' : 'user',
    });
    window.location.assign(result.authorization_url);
  };

  return (
    <div className={clsx(
      'rounded-lg border p-6',
      'bg-white dark:bg-gray-900',
      'border-gray-200 dark:border-gray-800',
      'shadow-sm'
    )}>
      <div className="space-y-6">
        {/* Email Field */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Email
          </label>
          {isAdmin ? (
            <input
              type="email"
              value={editedData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={clsx(
                'w-full px-3 py-2 rounded-lg border',
                'bg-white dark:bg-gray-800',
                'text-gray-900 dark:text-gray-100',
                'border-gray-300 dark:border-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                'transition-colors duration-150'
              )}
            />
          ) : (
            <p className="px-3 py-2 text-gray-600 dark:text-gray-400">
              {currentUser.email}
            </p>
          )}
        </div>

        {/* First Name Field */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            First Name
          </label>
          {isAdmin ? (
            <input
              type="text"
              value={editedData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              className={clsx(
                'w-full px-3 py-2 rounded-lg border',
                'bg-white dark:bg-gray-800',
                'text-gray-900 dark:text-gray-100',
                'border-gray-300 dark:border-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                'transition-colors duration-150'
              )}
            />
          ) : (
            <p className="px-3 py-2 text-gray-600 dark:text-gray-400">
              {currentUser.first_name}
            </p>
          )}
        </div>

        {/* Last Name Field */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Last Name
          </label>
          {isAdmin ? (
            <input
              type="text"
              value={editedData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              className={clsx(
                'w-full px-3 py-2 rounded-lg border',
                'bg-white dark:bg-gray-800',
                'text-gray-900 dark:text-gray-100',
                'border-gray-300 dark:border-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                'transition-colors duration-150'
              )}
            />
          ) : (
            <p className="px-3 py-2 text-gray-600 dark:text-gray-400">
              {currentUser.last_name}
            </p>
          )}
        </div>

        {/* Status Field (Read-only) */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Status
          </label>
          <p className="px-3 py-2 text-gray-600 dark:text-gray-400">
            {currentUser.status}
          </p>
        </div>

        {/* Is Admin Badge (Read-only) */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Role
          </label>
          <div className="px-3 py-2">
            {isAdmin ? (
              <span className={clsx(
                'inline-block px-3 py-1 rounded-full text-sm font-medium',
                'bg-purple-100 dark:bg-purple-900',
                'text-purple-800 dark:text-purple-200'
              )}>
                Administrator
              </span>
            ) : (
              <span className={clsx(
                'inline-block px-3 py-1 rounded-full text-sm font-medium',
                'bg-gray-100 dark:bg-gray-800',
                'text-gray-800 dark:text-gray-200'
              )}>
                User
              </span>
            )}
          </div>
        </div>

        <PlanGate feature="slack" fallback={
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
            Slack integration requires the Starter plan or above.{' '}
            <a href="https://ez-flow.io/pricing" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Upgrade
            </a>
          </div>
        }>
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Slack approvals</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Link Slack for your active tenant to receive approval reminders in direct messages.
              </p>
              {!slackConnection?.is_configured && (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  Slack is not configured for this environment yet.
                </p>
              )}
              {slackConnection?.is_connected && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  Connected to {slackConnection.team_name || slackConnection.team_id || 'your Slack workspace'}.
                </div>
              )}
              {!slackConnection?.is_connected && slackConnection?.is_configured && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  No Slack account linked for tenant {currentUser.active_tenant?.name || 'this workspace'}.
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {slackConnection?.is_connected ? (
                <button
                  type="button"
                  onClick={() => disconnectSlack.mutate()}
                  disabled={disconnectSlack.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {disconnectSlack.isPending ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectSlack}
                  disabled={!slackConnection?.is_configured || connectSlack.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg',
                    'bg-blue-600 text-white',
                    'hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {connectSlack.isPending ? 'Opening Slack...' : 'Connect Slack'}
                </button>
              )}
            </div>
          </div>
        </div>
        </PlanGate>

        <PlanGate feature="teams" fallback={
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
            Teams integration requires the Starter plan or above.{' '}
            <a href="https://ez-flow.io/pricing" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Upgrade
            </a>
          </div>
        }>
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Teams approvals</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Link Microsoft Teams for your active tenant to receive approval reminders in direct messages.
              </p>
              {!teamsConnection?.is_configured && (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  Teams is not configured for this environment yet.
                </p>
              )}
              {teamsConnection?.is_connected && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  Connected as {teamsConnection.email || teamsConnection.upn || 'your Teams account'}.
                </div>
              )}
              {!teamsConnection?.is_connected && teamsConnection?.is_configured && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  No Teams account linked for tenant {currentUser.active_tenant?.name || 'this workspace'}.
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {teamsConnection?.is_connected ? (
                <button
                  type="button"
                  onClick={() => disconnectTeams.mutate()}
                  disabled={disconnectTeams.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {disconnectTeams.isPending ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectTeams}
                  disabled={!teamsConnection?.is_configured || connectTeams.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg',
                    'bg-blue-600 text-white',
                    'hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {connectTeams.isPending ? 'Opening Teams...' : 'Connect Teams'}
                </button>
              )}
            </div>
          </div>
        </div>
        </PlanGate>

        <PlanGate feature="google_chat" fallback={
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
            Google Chat integration requires the Starter plan or above.{' '}
            <a href="https://ez-flow.io/pricing" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Upgrade
            </a>
          </div>
        }>
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Google Chat approvals</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Link Google Chat for your active tenant to receive approval reminders in direct messages.
              </p>
              {!googleChatConnection?.is_configured && (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  Google Chat is not configured for this environment yet.
                </p>
              )}
              {googleChatConnection?.is_connected && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  Connected as {googleChatConnection.email || googleChatConnection.display_name || 'your Google Chat account'}.
                </div>
              )}
              {!googleChatConnection?.is_connected && googleChatConnection?.is_configured && (
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  No Google Chat account linked for tenant {currentUser.active_tenant?.name || 'this workspace'}.
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {googleChatConnection?.is_connected ? (
                <button
                  type="button"
                  onClick={() => disconnectGoogleChat.mutate()}
                  disabled={disconnectGoogleChat.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {disconnectGoogleChat.isPending ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectGoogleChat}
                  disabled={!googleChatConnection?.is_configured || connectGoogleChat.isPending}
                  className={clsx(
                    'px-4 py-2 rounded-lg',
                    'bg-blue-600 text-white',
                    'hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {connectGoogleChat.isPending ? 'Opening Google...' : 'Connect Google Chat'}
                </button>
              )}
            </div>
          </div>
        </div>
        </PlanGate>

        {/* Action Buttons (visible only for admins with changes) */}
        {isAdmin && hasChanges && (
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg',
                'bg-blue-600 text-white',
                'hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors duration-150',
                'font-medium'
              )}
            >
              {isSaving && <Loader size={16} className="animate-spin" />}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className={clsx(
                'px-4 py-2 rounded-lg',
                'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                'hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors duration-150',
                'font-medium'
              )}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Info Note */}
      {!isAdmin && (
        <div className={clsx(
          'mt-6 rounded-lg border p-4',
          'bg-blue-50 dark:bg-blue-950',
          'border-blue-200 dark:border-blue-800'
        )}>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            You are viewing your profile as a regular user. Contact an administrator if you need to update your information.
          </p>
        </div>
      )}

      {/* Password Change Section */}
      <PasswordChangeSection />
    </div>
  );
};
